import { useState } from "react";
import { Tag, Check, Copy, Users, BadgePercent, Calendar, ExternalLink, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const fmt = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return null; }
};

export const OfferCard = ({ item }) => {
  const [copied, setCopied] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(item.promo_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("[OfferCard] clipboard unavailable", e);
    }
  };

  const start = fmt(item.start_at);
  const end = fmt(item.end_at);

  return (
    <div data-testid="offer-card" className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-colors duration-200 hover:border-[hsl(var(--brand-red))]">
      {item.image_url && (
        <div className="relative h-44 overflow-hidden">
          <img src={item.image_url} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          {item.badge_text && (
            <span className="absolute left-3 top-3 rounded-md bg-brand px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white">{item.badge_text}</span>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col p-6">
        <h3 className="display-title text-2xl leading-tight">{item.title}</h3>
        {item.description && <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>}

        {item.discount_details && (
          <p className="mt-4 flex items-start gap-2 text-sm font-semibold text-foreground"><BadgePercent className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{item.discount_details}</p>
        )}
        {item.who_qualifies && (
          <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground"><Users className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{item.who_qualifies}</p>
        )}
        {(start || end) && (
          <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><Calendar className="h-3.5 w-3.5 text-brand" />{start && end ? `${start} \u2013 ${end}` : start || end}</p>
        )}

        {item.promo_code && (
          <button type="button" onClick={copyCode} data-testid="offer-promo-code" className="mt-4 flex items-center justify-between gap-2 rounded-md border border-dashed border-[hsl(var(--brand-red))] bg-brand/5 px-3 py-2.5 text-left transition-colors hover:bg-brand/10">
            <span className="flex items-center gap-2"><Tag className="h-4 w-4 text-brand" /><span className="font-mono text-sm font-bold tracking-wider">{item.promo_code}</span></span>
            <span className="flex items-center gap-1 text-xs font-semibold uppercase text-brand">{copied ? (<><Check className="h-3.5 w-3.5" /> Copied</>) : (<><Copy className="h-3.5 w-3.5" /> Copy</>)}</span>
          </button>
        )}

        <div className="flex-1" />

        {item.terms && (
          <div className="mt-4">
            <button type="button" onClick={() => setShowTerms((v) => !v)} data-testid="offer-terms-toggle" className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
              Terms <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showTerms && "rotate-180")} />
            </button>
            {showTerms && <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{item.terms}</p>}
          </div>
        )}

        {item.cta_url && (
          <a href={item.cta_url} target="_blank" rel="noopener noreferrer" data-testid="offer-cta-btn" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-[hsl(var(--brand-red-deep))] active:scale-[0.98]">
            {item.cta_label || "Claim Offer"} <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};
